// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Debris Ops Store (Zustand)
// Manages load tickets, invoices, audit log, photos
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords } from '../api/client';
import { TABLES, FIELDS, createRecord, updateRecord, deleteRecord } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import type {
  DebrisLoadTicket, DebrisInvoice, DebrisAuditEntry, DebrisPhoto, DebrisRequest,
} from '../types/debris';

// ── Field key shortcuts ──────────────────────────────────────
const FL = FIELDS.debrisLoadTickets;
const FI = FIELDS.debrisInvoices;
const FP = FIELDS.debrisPhotos;
// Note: debrisAuditLog and debrisRequests tables do not exist in V2

const TL = TABLES.debrisLoadTickets;
const TI = TABLES.debrisInvoices;
const TP = TABLES.debrisPhotos;
// Note: debrisAuditLog and debrisRequests tables do not exist in V2

function getToken() {
  return useAuthStore.getState().token ?? undefined;
}

// ── Safety: strip base64 photo data from stops JSON if it's too large for Ignatius TEXT (65K) ──
function stripStopPhotosIfTooLarge(json: string): string {
  if (!json || json.length < 50000) return json;
  try {
    const stops = JSON.parse(json);
    if (!Array.isArray(stops)) return json;
    const stripped = stops.map((s: Record<string, unknown>) => {
      const out = { ...s };
      delete out.beforePhotoData;
      delete out.afterPhotoData;
      return out;
    });
    return JSON.stringify(stripped);
  } catch { return json; }
}

// ── Notes encoding for fields that don't have V2 columns ──
// Stashes extra fields into the notes TEXT field using triple-pipe markers.
// Decoded on read, encoded on write.
const TICKET_META_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;

function decodeTicketNotes(raw: string): { notes: string; meta: Record<string, string> } {
  if (!raw) return { notes: '', meta: {} };
  const m = raw.match(TICKET_META_RE);
  if (!m) return { notes: raw, meta: {} };
  try {
    const meta = JSON.parse(m[1]);
    const notes = raw.replace(TICKET_META_RE, '').trim();
    return { notes, meta };
  } catch {
    return { notes: raw, meta: {} };
  }
}

function encodeTicketNotes(userNotes: string, metaFields: Record<string, string>): string {
  // Remove any existing meta block from userNotes first
  const clean = userNotes.replace(TICKET_META_RE, '').trim();
  // Only add meta if there are non-empty values
  const nonEmpty = Object.fromEntries(Object.entries(metaFields).filter(([, v]) => v !== '' && v !== undefined));
  if (Object.keys(nonEmpty).length === 0) return clean;
  const metaStr = JSON.stringify(nonEmpty);
  return clean ? `${clean}\n|||TMETA|||${metaStr}|||ENDTMETA|||` : `|||TMETA|||${metaStr}|||ENDTMETA|||`;
}

// ── Row mappers ──────────────────────────────────────────────

function mapTicket(row: Record<string, unknown>): DebrisLoadTicket {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  // Decode meta from notes field (legacy encoding for fields without V2 columns)
  const rawNotes = String(row[FL.notes] ?? '');
  const { notes, meta } = decodeTicketNotes(rawNotes);
  return {
    id: rid, Rid: rid,
    incidentId: String(row[FL.incidentId] ?? ''),
    ticketNumber: String(row[FL.ticketNumber] ?? ''),
    truckId: String(row[FL.truckId] ?? ''),
    truckNumber: meta.truckNumber || '',
    crewId: meta.crewId || '',
    contractorId: String(row[FL.contractorId] ?? ''),
    zoneId: String(row[FL.zoneId] ?? ''),
    disposalSiteId: String(row[FL.disposalSiteId] ?? ''),
    debrisType: String(row[FL.debrisType] ?? ''),
    measurementMode: String(row[FL.measurementMode] ?? '') || meta.measurementMode || 'volumetric',
    loadPercentage: meta.loadPercentage || '',
    calculatedCubicYards: String(row[FL.calculatedCubicYards] ?? ''),
    unitCount: String(row[FL.unitCount] ?? '') || meta.unitCount || '',
    unitSubcategory: meta.unitSubcategory || '',
    diameterRange: meta.diameterRange || '',
    weight: String(row[FL.weight] ?? '') || meta.weight || '',
    weightUnit: meta.weightUnit || '',
    scaleTicketNumber: meta.scaleTicketNumber || '',
    pickupLat: meta.pickupLat || '',
    pickupLng: meta.pickupLng || '',
    pickupAddress: meta.pickupAddress || '',
    pickupTime: meta.pickupTime || '',
    pickupStopsJson: String(row[FL.pickupStopsJson] ?? '') || meta.pickupStopsJson || '',
    disposalTime: meta.disposalTime || '',
    loadingMonitorName: String(row[FL.loadingMonitorName] ?? ''),
    loadingMonitorSignature: String(row[FL.loadingMonitorSignature] ?? ''),
    driverPickupSignature: String(row[FL.driverPickupSignature] ?? ''),
    loadingLoadPct: String(row[FL.loadingLoadPct] ?? ''),
    towerLoadPct: String(row[FL.towerLoadPct] ?? ''),
    monitorName: String(row[FL.towerMonitorName] ?? ''),
    monitorSignature: String(row[FL.towerMonitorSignature] ?? ''),
    driverSignature: String(row[FL.driverDeliverySignature] ?? ''),
    verifiedAt: String(row[FL.verifiedAt] ?? '') || meta.verifiedAt || '',
    handLoaded: String(row[FL.handLoaded] ?? ''),
    qrCode: meta.qrCode || '',
    status: (String(row[FL.status] ?? '') || 'draft') as DebrisLoadTicket['status'],
    photoBeforeUrl: '',
    photoAfterUrl: '',
    photoLoadUrl: '',
    notes,
  };
}

const INVOICE_META_RE = /\|\|\|IMETA\|\|\|([\s\S]*?)\|\|\|ENDIMETA\|\|\|/;

function mapInvoice(row: Record<string, unknown>): DebrisInvoice {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  const rawNotes = String(row[FI.reviewerNotes] ?? '');
  const metaMatch = rawNotes.match(INVOICE_META_RE);
  let meta: Record<string, string> = {};
  let notes = rawNotes;
  if (metaMatch) {
    try { meta = JSON.parse(metaMatch[1]); } catch { /* ignore */ }
    notes = rawNotes.replace(INVOICE_META_RE, '').trim();
  }
  return {
    id: rid, Rid: rid,
    incidentId: String(row[FI.incidentId] ?? ''),
    contractorId: String(row[FI.contractorId] ?? ''),
    invoiceNumber: String(row[FI.invoiceNumber] ?? ''),
    periodStart: String(row[FI.periodStart] ?? ''),
    periodEnd: String(row[FI.periodEnd] ?? ''),
    totalCubicYards: meta.totalCubicYards || '',
    totalAmount: String(row[FI.totalAmount] ?? ''),
    ticketIds: meta.ticketIds || '[]',
    lineItems: String(row[FI.lineItems] ?? '') || '[]',
    status: (String(row[FI.status] ?? '') || 'draft') as DebrisInvoice['status'],
    approvedBy: meta.approvedBy || '',
    notes,
  };
}

function mapAudit(row: Record<string, unknown>): DebrisAuditEntry {
  // debrisAuditLog table does not exist in V2; this mapper is a placeholder
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: String(row.incidentId ?? ''),
    entityType: String(row.entityType ?? ''),
    entityId: String(row.entityId ?? ''),
    action: String(row.action ?? ''),
    fieldChanged: String(row.fieldChanged ?? ''),
    oldValue: String(row.oldValue ?? ''),
    newValue: String(row.newValue ?? ''),
    actorName: String(row.actorName ?? ''),
    actorEmail: String(row.actorEmail ?? ''),
    actorRole: String(row.actorRole ?? ''),
  };
}

function mapPhoto(row: Record<string, unknown>): DebrisPhoto {
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    parentId: String(row[FP.ticketId] ?? ''),
    parentType: 'ticket',
    incidentId: String(row[FP.incidentId] ?? ''),
    photoUrl: (() => { const v = String(row[FP.photo] ?? ''); return v.startsWith('data:') || v.startsWith('http') || v.startsWith('/') ? v : ''; })(),
    caption: String(row[FP.caption] ?? ''),
    geoLat: String(row[FP.latitude] ?? ''),
    geoLng: String(row[FP.longitude] ?? ''),
    takenAt: String(row[FP.takenAt] ?? ''),
    uploadedBy: String(row[FP.takenBy] ?? ''),
  };
}

function mapRequest(row: Record<string, unknown>): DebrisRequest {
  // debrisRequests table does not exist in V2; this mapper is a placeholder
  const rid = Number(row.record_id) || Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    incidentId: String(row.incidentId ?? ''),
    assessmentId: String(row.assessmentId ?? ''),
    address: String(row.address ?? ''),
    latitude: String(row.latitude ?? ''),
    longitude: String(row.longitude ?? ''),
    damageCategory: String(row.damageCategory ?? ''),
    damageTypes: String(row.damageTypes ?? '') || '[]',
    description: String(row.description ?? ''),
    status: (String(row.status ?? '') || 'pending') as DebrisRequest['status'],
    taggedBy: String(row.taggedBy ?? ''),
    taggedAt: String(row.taggedAt ?? ''),
    actionedBy: String(row.actionedBy ?? ''),
    actionedAt: String(row.actionedAt ?? ''),
    createdTicketId: String(row.createdTicketId ?? ''),
    notes: String(row.notes ?? ''),
  };
}

const PAGE_SIZE = 100;

// ── Store Interface ──────────────────────────────────────────

interface DebrisOpsStore {
  loadTickets: DebrisLoadTicket[];
  ticketsTotal: number;
  ticketsPage: number;
  invoices: DebrisInvoice[];
  auditLog: DebrisAuditEntry[];
  photos: DebrisPhoto[];
  requests: DebrisRequest[];
  loadingTickets: boolean;
  loadingInvoices: boolean;
  loadingAudit: boolean;
  loadingPhotos: boolean;
  loadingRequests: boolean;
  error: string | null;

  // Load Tickets
  loadLoadTickets: () => Promise<void>;
  setTicketsPage: (page: number) => void;
  createLoadTicket: (data: Partial<DebrisLoadTicket>) => Promise<DebrisLoadTicket | null>;
  updateLoadTicket: (id: number, data: Partial<DebrisLoadTicket>) => Promise<void>;
  deleteLoadTicket: (id: number) => Promise<void>;

  // Invoices
  loadInvoices: () => Promise<void>;
  createInvoice: (data: Partial<DebrisInvoice>) => Promise<DebrisInvoice | null>;
  updateInvoice: (id: number, data: Partial<DebrisInvoice>) => Promise<void>;
  deleteInvoice: (id: number) => Promise<void>;

  // Requests (Impact → Debris handoff)
  loadRequests: () => Promise<void>;
  createRequest: (data: Partial<DebrisRequest>) => Promise<DebrisRequest | null>;
  updateRequest: (id: number, data: Partial<DebrisRequest>) => Promise<void>;
  deleteRequest: (id: number) => Promise<void>;

  // Audit Log
  loadAuditLog: () => Promise<void>;
  createAuditEntry: (data: Partial<DebrisAuditEntry>) => Promise<void>;

  // Photos
  loadPhotos: () => Promise<void>;
  createPhoto: (data: Partial<DebrisPhoto>) => Promise<DebrisPhoto | null>;
  deletePhoto: (id: number) => Promise<void>;
}

// ── Store Implementation ─────────────────────────────────────

export const useDebrisOpsStore = create<DebrisOpsStore>((set, get) => ({
  loadTickets: [],
  ticketsTotal: 0,
  ticketsPage: 1,
  invoices: [],
  auditLog: [],
  photos: [],
  requests: [],
  loadingTickets: false,
  loadingInvoices: false,
  loadingAudit: false,
  loadingPhotos: false,
  loadingRequests: false,
  error: null,

  // ── Load Tickets ──────────────────────────────────────────

  loadLoadTickets: async () => {
    set({ loadingTickets: true, error: null });
    try {
      const { ticketsPage } = get();
      const res = await fetchRecords<Record<string, unknown>>(TL, {
        limit: PAGE_SIZE,
        offset: (ticketsPage - 1) * PAGE_SIZE,
      });
      set({ loadTickets: res.records.map(mapTicket), ticketsTotal: res.total, loadingTickets: false });
    } catch (err) {
      set({ loadingTickets: false, error: err instanceof Error ? err.message : 'Failed to load tickets' });
    }
  },

  setTicketsPage: (page) => {
    set({ ticketsPage: page });
    get().loadLoadTickets();
  },

  createLoadTicket: async (data) => {
    // Encode non-V2 fields into notes meta
    const notesWithMeta = encodeTicketNotes(data.notes ?? '', {
      truckNumber: data.truckNumber ?? '',
      crewId: data.crewId ?? '',
      loadPercentage: data.loadPercentage ?? '',
      pickupLat: data.pickupLat ?? '',
      pickupLng: data.pickupLng ?? '',
      pickupAddress: data.pickupAddress ?? '',
      pickupTime: data.pickupTime ?? '',
      disposalTime: data.disposalTime ?? '',
      photoBeforeUrl: data.photoBeforeUrl ?? '',
      photoAfterUrl: data.photoAfterUrl ?? '',
      photoLoadUrl: data.photoLoadUrl ?? '',
      unitSubcategory: data.unitSubcategory ?? '',
      diameterRange: data.diameterRange ?? '',
      weightUnit: data.weightUnit ?? '',
      scaleTicketNumber: data.scaleTicketNumber ?? '',
      qrCode: data.qrCode ?? '',
    });
    // POST only V2-column fields (first 10)
    const fields: Array<[string, string]> = [
      [FL.incidentId, data.incidentId ?? ''],
      [FL.ticketNumber, data.ticketNumber ?? ''],
      [FL.truckId, data.truckId ?? ''],
      [FL.contractorId, data.contractorId ?? ''],
      [FL.zoneId, data.zoneId ?? ''],
      [FL.disposalSiteId, data.disposalSiteId ?? ''],
      [FL.debrisType, data.debrisType ?? ''],
      [FL.measurementMode, data.measurementMode ?? ''],
      [FL.status, data.status ?? 'draft'],
      [FL.notes, notesWithMeta],
    ];
    const rid = await createRecord(TL, fields, getToken());
    if (rid) {
      const extra: Array<[string, string]> = [
        [FL.calculatedCubicYards, data.calculatedCubicYards ?? ''],
        [FL.unitCount, data.unitCount ?? ''],
        [FL.weight, data.weight ?? ''],
        [FL.pickupStopsJson, stripStopPhotosIfTooLarge(data.pickupStopsJson ?? '')],
        [FL.loadingMonitorName, data.loadingMonitorName ?? ''],
        [FL.loadingMonitorSignature, data.loadingMonitorSignature ?? ''],
        [FL.driverPickupSignature, data.driverPickupSignature ?? ''],
        [FL.loadingLoadPct, data.loadingLoadPct ?? ''],
        [FL.towerLoadPct, data.towerLoadPct ?? ''],
        [FL.handLoaded, data.handLoaded ?? ''],
      ];
      await updateRecord(TL, rid, extra, getToken());
      const extra2: Array<[string, string]> = [
        [FL.verifiedAt, data.verifiedAt ?? ''],
        [FL.towerMonitorName, data.monitorName ?? (data as any).towerMonitorName ?? ''],
        [FL.towerMonitorSignature, data.monitorSignature ?? (data as any).towerMonitorSignature ?? ''],
        [FL.driverDeliverySignature, data.driverSignature ?? (data as any).driverDeliverySignature ?? ''],
      ];
      await updateRecord(TL, rid, extra2, getToken());
    }
    await get().loadLoadTickets();
    return get().loadTickets.find(t => t.id === rid) ?? null;
  },

  updateLoadTicket: async (id, data) => {
    // Get the existing ticket to preserve its current meta
    const existing = get().loadTickets.find(t => t.id === id);
    const existingNotes = existing?.notes ?? '';

    // Collect all meta fields (use incoming data values, fall back to existing)
    const metaFields: Record<string, string> = {
      truckNumber: data.truckNumber ?? existing?.truckNumber ?? '',
      crewId: data.crewId ?? existing?.crewId ?? '',
      loadPercentage: data.loadPercentage ?? existing?.loadPercentage ?? '',
      pickupLat: data.pickupLat ?? existing?.pickupLat ?? '',
      pickupLng: data.pickupLng ?? existing?.pickupLng ?? '',
      pickupAddress: data.pickupAddress ?? existing?.pickupAddress ?? '',
      pickupTime: data.pickupTime ?? existing?.pickupTime ?? '',
      disposalTime: data.disposalTime ?? existing?.disposalTime ?? '',
      photoBeforeUrl: data.photoBeforeUrl ?? existing?.photoBeforeUrl ?? '',
      photoAfterUrl: data.photoAfterUrl ?? existing?.photoAfterUrl ?? '',
      photoLoadUrl: data.photoLoadUrl ?? existing?.photoLoadUrl ?? '',
      unitSubcategory: data.unitSubcategory ?? existing?.unitSubcategory ?? '',
      diameterRange: data.diameterRange ?? existing?.diameterRange ?? '',
      weightUnit: data.weightUnit ?? existing?.weightUnit ?? '',
      scaleTicketNumber: data.scaleTicketNumber ?? existing?.scaleTicketNumber ?? '',
      qrCode: data.qrCode ?? existing?.qrCode ?? '',
    };
    const userNotes = data.notes !== undefined ? data.notes : existingNotes;
    const notesWithMeta = encodeTicketNotes(userNotes, metaFields);

    // Build V2-column-only fields array
    const fields: Array<[string, string]> = [
      [FL.notes, notesWithMeta],
    ];
    if (data.ticketNumber !== undefined) fields.push([FL.ticketNumber, data.ticketNumber]);
    if (data.truckId !== undefined) fields.push([FL.truckId, data.truckId]);
    if (data.contractorId !== undefined) fields.push([FL.contractorId, data.contractorId]);
    if (data.zoneId !== undefined) fields.push([FL.zoneId, data.zoneId]);
    if (data.disposalSiteId !== undefined) fields.push([FL.disposalSiteId, data.disposalSiteId]);
    if (data.debrisType !== undefined) fields.push([FL.debrisType, data.debrisType]);
    if (data.measurementMode !== undefined) fields.push([FL.measurementMode, data.measurementMode]);
    if (data.calculatedCubicYards !== undefined) fields.push([FL.calculatedCubicYards, data.calculatedCubicYards]);
    if (data.unitCount !== undefined) fields.push([FL.unitCount, data.unitCount]);
    if (data.weight !== undefined) fields.push([FL.weight, data.weight]);
    if (data.status !== undefined) fields.push([FL.status, data.status]);
    if (data.pickupStopsJson !== undefined) fields.push([FL.pickupStopsJson, stripStopPhotosIfTooLarge(data.pickupStopsJson)]);
    if (data.loadingMonitorName !== undefined) fields.push([FL.loadingMonitorName, data.loadingMonitorName]);
    if (data.loadingMonitorSignature !== undefined) fields.push([FL.loadingMonitorSignature, data.loadingMonitorSignature]);
    if (data.driverPickupSignature !== undefined) fields.push([FL.driverPickupSignature, data.driverPickupSignature]);
    if (data.loadingLoadPct !== undefined) fields.push([FL.loadingLoadPct, data.loadingLoadPct]);
    if (data.towerLoadPct !== undefined) fields.push([FL.towerLoadPct, data.towerLoadPct]);
    if (data.handLoaded !== undefined) fields.push([FL.handLoaded, data.handLoaded]);
    if (data.verifiedAt !== undefined) fields.push([FL.verifiedAt, data.verifiedAt]);
    // Tower monitor fields — stored in V2 columns (not meta)
    if (data.monitorName !== undefined) fields.push([FL.towerMonitorName, data.monitorName]);
    if (data.monitorSignature !== undefined) fields.push([FL.towerMonitorSignature, data.monitorSignature]);
    if (data.driverSignature !== undefined) fields.push([FL.driverDeliverySignature, data.driverSignature]);
    if ((data as any).towerMonitorName !== undefined) fields.push([FL.towerMonitorName, (data as any).towerMonitorName]);
    if ((data as any).towerMonitorSignature !== undefined) fields.push([FL.towerMonitorSignature, (data as any).towerMonitorSignature]);
    if ((data as any).driverDeliverySignature !== undefined) fields.push([FL.driverDeliverySignature, (data as any).driverDeliverySignature]);

    if (fields.length > 0) {
      for (let i = 0; i < fields.length; i += 10) {
        await updateRecord(TL, id, fields.slice(i, i + 10), getToken());
      }
      await get().loadLoadTickets();
    }
  },

  deleteLoadTicket: async (id) => {
    if (!id) return;
    await deleteRecord(TL, id, getToken());
    set({ loadTickets: get().loadTickets.filter(t => t.id !== id) });
  },

  // ── Invoices ──────────────────────────────────────────────

  loadInvoices: async () => {
    set({ loadingInvoices: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TI, { limit: 200 });
      set({ invoices: res.records.map(mapInvoice), loadingInvoices: false });
    } catch (err) {
      set({ loadingInvoices: false, error: err instanceof Error ? err.message : 'Failed to load invoices' });
    }
  },

  createInvoice: async (data) => {
    // Note: totalCubicYards, ticketIds, approvedBy have no V2 columns — stored in reviewerNotes as meta
    const metaStr = JSON.stringify({
      totalCubicYards: data.totalCubicYards ?? '',
      ticketIds: data.ticketIds ?? '[]',
      approvedBy: data.approvedBy ?? '',
    });
    const notesWithMeta = data.notes ? `${data.notes}\n|||IMETA|||${metaStr}|||ENDIMETA|||` : `|||IMETA|||${metaStr}|||ENDIMETA|||`;
    const fields: Array<[string, string]> = [
      [FI.incidentId, data.incidentId ?? ''],
      [FI.contractorId, data.contractorId ?? ''],
      [FI.invoiceNumber, data.invoiceNumber ?? ''],
      [FI.periodStart, data.periodStart ?? ''],
      [FI.periodEnd, data.periodEnd ?? ''],
      [FI.totalAmount, data.totalAmount ?? ''],
      [FI.lineItems, data.lineItems ?? '[]'],
      [FI.status, data.status ?? 'draft'],
      [FI.reviewerNotes, notesWithMeta],
    ];
    const rid = await createRecord(TI, fields, getToken());
    await get().loadInvoices();
    return get().invoices.find(i => i.id === rid) ?? null;
  },

  updateInvoice: async (id, data) => {
    const fields: Array<[string, string]> = [];
    if (data.contractorId !== undefined) fields.push([FI.contractorId, data.contractorId]);
    if (data.invoiceNumber !== undefined) fields.push([FI.invoiceNumber, data.invoiceNumber]);
    if (data.periodStart !== undefined) fields.push([FI.periodStart, data.periodStart]);
    if (data.periodEnd !== undefined) fields.push([FI.periodEnd, data.periodEnd]);
    if (data.totalAmount !== undefined) fields.push([FI.totalAmount, data.totalAmount]);
    if (data.lineItems !== undefined) fields.push([FI.lineItems, data.lineItems]);
    if (data.status !== undefined) fields.push([FI.status, data.status]);
    // Store non-V2-column fields in reviewerNotes meta
    if (data.totalCubicYards !== undefined || data.ticketIds !== undefined || data.approvedBy !== undefined || data.notes !== undefined) {
      const existing = get().invoices.find(i => i.id === id);
      const metaStr = JSON.stringify({
        totalCubicYards: data.totalCubicYards ?? existing?.totalCubicYards ?? '',
        ticketIds: data.ticketIds ?? existing?.ticketIds ?? '[]',
        approvedBy: data.approvedBy ?? existing?.approvedBy ?? '',
      });
      const userNotes = data.notes !== undefined ? data.notes : (existing?.notes ?? '');
      const notesWithMeta = userNotes ? `${userNotes}\n|||IMETA|||${metaStr}|||ENDIMETA|||` : `|||IMETA|||${metaStr}|||ENDIMETA|||`;
      fields.push([FI.reviewerNotes, notesWithMeta]);
    }
    if (fields.length > 0) {
      await updateRecord(TI, id, fields, getToken());
      await get().loadInvoices();
    }
  },

  deleteInvoice: async (id) => {
    if (!id) return;
    await deleteRecord(TI, id, getToken());
    set({ invoices: get().invoices.filter(i => i.id !== id) });
  },

  // ── Requests (Impact → Debris) ───────────────────────────
  // Note: debrisRequests table does not exist in V2; these are no-ops

  loadRequests: async () => {
    set({ loadingRequests: false });
  },

  createRequest: async (_data) => {
    // debrisRequests table not in V2 — return null
    return null;
  },

  updateRequest: async (_id, _data) => {
    // debrisRequests table not in V2 — no-op
  },

  deleteRequest: async (_id) => {
    // debrisRequests table not in V2 — no-op
    set({ requests: [] });
  },

  // ── Audit Log ─────────────────────────────────────────────
  // Note: debrisAuditLog table does not exist in V2; these are no-ops

  loadAuditLog: async () => {
    set({ loadingAudit: false, auditLog: [] });
  },

  createAuditEntry: async (_data) => {
    // debrisAuditLog table not in V2 — no-op
  },

  // ── Photos ────────────────────────────────────────────────

  loadPhotos: async () => {
    set({ loadingPhotos: true, error: null });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TP, { limit: 500 });
      set({ photos: res.records.map(mapPhoto), loadingPhotos: false });
    } catch (err) {
      set({ loadingPhotos: false, error: err instanceof Error ? err.message : 'Failed to load photos' });
    }
  },

  createPhoto: async (data) => {
    // V2 debrisPhotos columns: incidentId, ticketId, zoneId, photo, caption, category, latitude, longitude, takenBy, takenAt
    // parentId maps to ticketId; photoUrl maps to photo; geoLat/geoLng map to latitude/longitude; uploadedBy maps to takenBy
    const fields: Array<[string, string]> = [
      [FP.incidentId, data.incidentId ?? ''],
      [FP.ticketId, data.parentId ?? ''],
      [FP.photo, data.photoUrl ?? ''],
      [FP.caption, data.caption ?? ''],
      [FP.latitude, data.geoLat ?? ''],
      [FP.longitude, data.geoLng ?? ''],
      [FP.takenAt, data.takenAt ?? ''],
      [FP.takenBy, data.uploadedBy ?? ''],
    ];
    const rid = await createRecord(TP, fields, getToken());
    await get().loadPhotos();
    return get().photos.find(p => p.id === rid) ?? null;
  },

  deletePhoto: async (id) => {
    await deleteRecord(TP, id, getToken());
    set({ photos: get().photos.filter(p => p.id !== id) });
  },
}));
